import React, { useMemo, useState } from 'react';
import { CalendarDays, Clock3, FileText, MoreVertical, Pencil, Search, Trash2 } from 'lucide-react';
import { meetlyMeetingTitle, normalizeMeetlyMeetingStatus, type MeetlyMeeting, type MeetlyMeetingUpdate } from '../../api/meetly';
import { formatDurationLabel } from '../../utils/meetlyAudio';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import MeetlyMeetingEditor from './MeetlyMeetingEditor';

interface MeetlyMeetingListProps {
  meetings: MeetlyMeeting[];
  onSelect: (meetingId: string) => void;
  onUpdate: (meetingId: string, update: MeetlyMeetingUpdate) => Promise<void>;
  onDelete: (meetingId: string) => Promise<void>;
}

const meetingDate = (meeting: MeetlyMeeting) => {
  const parsed = new Date(meeting.completed_at || meeting.created_at);
  return Number.isNaN(parsed.getTime())
    ? 'Date unavailable'
    : parsed.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

const summarySnippet = (meeting: MeetlyMeeting) => {
  const text = (meeting.summary || '').replace(/[#*_`-]/g, '').replace(/\s+/g, ' ').trim();
  return text || null;
};

const STATUS_STYLE: Record<string, { dot: string; label: string; text: string }> = {
  COMPLETED: { dot: 'bg-emerald-500', label: 'Ready', text: 'text-emerald-700' },
  FAILED: { dot: 'bg-red-500', label: 'Failed', text: 'text-red-700' },
  SUBMITTING: { dot: 'bg-amber-500', label: 'Processing', text: 'text-amber-700' },
  QUEUED: { dot: 'bg-amber-500', label: 'Processing', text: 'text-amber-700' },
};

function MeetingCard({ meeting, onSelect, onEdit, onDelete }: { meeting: MeetlyMeeting; onSelect: (id: string) => void; onEdit: (meeting: MeetlyMeeting) => void; onDelete: (meeting: MeetlyMeeting) => void }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const status = STATUS_STYLE[normalizeMeetlyMeetingStatus(meeting.status)] || STATUS_STYLE.QUEUED;
  const snippet = summarySnippet(meeting);
  const actionCount = meeting.action_items?.length || 0;
  const title = meetlyMeetingTitle(meeting);

  return (
    <article
      onClick={() => onSelect(meeting.id)}
      className="group flex h-full cursor-pointer flex-col rounded-[var(--gt-radius-card)] border border-[var(--gt-border)] bg-[var(--gt-surface-0)] p-5 text-left transition-all duration-150 hover:-translate-y-0.5 hover:border-[var(--gt-primary)] hover:shadow-[var(--gt-elev-2)]"
    >
      <div className="flex items-start justify-between gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[var(--gt-accent-soft)] bg-[var(--gt-primary-container)] text-[var(--gt-primary)]">
          <FileText size={16} aria-hidden="true" />
        </span>
        <div className="flex items-center gap-1.5">
          <span className={`inline-flex items-center gap-1.5 rounded-[var(--gt-radius-pill)] bg-[var(--gt-surface-1)] px-2.5 py-1 text-[11px] font-semibold ${status.text}`}>
            <span className={`h-1.5 w-1.5 rounded-full ${status.dot}`} aria-hidden="true" />
            {status.label}
          </span>
          <div className="relative">
            <button type="button" aria-label={`Meeting actions for ${title}`} aria-haspopup="menu" aria-expanded={menuOpen} onClick={event => { event.stopPropagation(); setMenuOpen(open => !open); }} className="rounded-full p-1.5 text-[var(--gt-text-tertiary)] hover:bg-[var(--gt-surface-2)] hover:text-[var(--gt-text-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gt-primary)]">
              <MoreVertical size={17} aria-hidden="true" />
            </button>
            {menuOpen && (
              <div role="menu" className="absolute right-0 top-full z-20 mt-1 min-w-28 rounded-xl border border-[var(--gt-border)] bg-[var(--gt-surface-0)] p-1 shadow-[var(--gt-elev-2)]">
                <button type="button" role="menuitem" onClick={event => { event.stopPropagation(); setMenuOpen(false); onEdit(meeting); }} className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm hover:bg-[var(--gt-surface-1)]"><Pencil size={14} aria-hidden="true" /> Edit</button>
                <div role="separator" className="my-1 h-px bg-[var(--gt-divider)]" />
                <button type="button" role="menuitem" onClick={event => { event.stopPropagation(); setMenuOpen(false); onDelete(meeting); }} className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm text-red-700 hover:bg-red-50"><Trash2 size={14} aria-hidden="true" /> Delete</button>
              </div>
            )}
          </div>
        </div>
      </div>

      <h3 className="mt-4 line-clamp-2 text-base font-bold text-[var(--gt-text-primary)] group-hover:text-[var(--gt-primary)]">{title}</h3>
      <p className="mt-3 line-clamp-3 flex-1 text-sm leading-6 text-[var(--gt-text-secondary)]">{snippet || 'Summary not available yet.'}</p>
      <div className="mt-4 flex items-center justify-between border-t border-[var(--gt-divider)] pt-3 text-xs text-[var(--gt-text-tertiary)]">
        <span className="inline-flex items-center gap-3">
          <span className="inline-flex items-center gap-1.5"><CalendarDays size={13} aria-hidden="true" /> {meetingDate(meeting)}</span>
          {meeting.duration_seconds != null && <span className="inline-flex items-center gap-1.5"><Clock3 size={13} aria-hidden="true" /> {formatDurationLabel(meeting.duration_seconds)}</span>}
        </span>
        {actionCount > 0 && <span className="inline-flex items-center gap-1 rounded-full bg-[var(--gt-surface-2)] px-2 py-0.5 font-semibold text-[var(--gt-text-secondary)]">{actionCount} action{actionCount === 1 ? '' : 's'}</span>}
      </div>
    </article>
  );
}

export function MeetlyMeetingList({ meetings, onSelect, onUpdate, onDelete }: MeetlyMeetingListProps) {
  const [query, setQuery] = useState('');
  const [editingMeeting, setEditingMeeting] = useState<MeetlyMeeting | null>(null);
  const [deletingMeeting, setDeletingMeeting] = useState<MeetlyMeeting | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const filteredMeetings = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return meetings;
    return meetings.filter(meeting => `${meetlyMeetingTitle(meeting)} ${meeting.summary || ''}`.toLowerCase().includes(needle));
  }, [meetings, query]);

  const confirmDelete = async () => {
    if (!deletingMeeting || deleting) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await onDelete(deletingMeeting.id);
      setDeletingMeeting(null);
    } catch {
      setDeleteError('Meetly could not delete this meeting. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <section aria-label="Meetings">
      <label className="mb-5 flex max-w-sm items-center gap-2 rounded-[var(--gt-radius-input)] border border-[var(--gt-border)] bg-white px-3 py-2 text-sm text-[var(--gt-text-secondary)] transition-colors duration-150 focus-within:border-[var(--gt-primary)]">
        <Search size={15} aria-hidden="true" /><span className="sr-only">Search meetings</span>
        <input aria-label="Search meetings" value={query} onChange={event => setQuery(event.target.value)} className="min-w-0 flex-1 bg-transparent outline-none placeholder:text-[var(--gt-text-tertiary)]" placeholder="Search meetings..." />
      </label>
      {filteredMeetings.length ? <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">{filteredMeetings.map(meeting => <MeetingCard key={meeting.id} meeting={meeting} onSelect={onSelect} onEdit={setEditingMeeting} onDelete={meeting => { setDeleteError(null); setDeletingMeeting(meeting); }} />)}</div> : <p className="rounded-[var(--gt-radius-card)] border border-dashed border-[var(--gt-border-strong)] bg-[var(--gt-surface-1)] px-5 py-8 text-center text-sm text-[var(--gt-text-tertiary)]">No meetings match &ldquo;{query.trim()}&rdquo;. Try a shorter search.</p>}

      {editingMeeting && <MeetlyMeetingEditor meeting={editingMeeting} onClose={() => setEditingMeeting(null)} onSave={update => onUpdate(editingMeeting.id, update)} />}
      <AlertDialog open={Boolean(deletingMeeting)} onOpenChange={open => { if (!open && !deleting) setDeletingMeeting(null); }}>
        <AlertDialogContent className="w-[calc(100vw-2rem)] max-w-md rounded-[var(--gt-radius-card)] border-[var(--gt-border)] bg-[var(--gt-surface-0)] text-[var(--gt-text-primary)]">
          <AlertDialogHeader>
            <AlertDialogTitle className="gt-display">Delete meeting</AlertDialogTitle>
            <AlertDialogDescription className="text-[var(--gt-text-secondary)]">Delete &ldquo;{deletingMeeting ? meetlyMeetingTitle(deletingMeeting) : ''}&rdquo;? This removes its private summary and suggested actions.</AlertDialogDescription>
          </AlertDialogHeader>
          {deleteError && <p role="alert" className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{deleteError}</p>}
          <AlertDialogFooter className="gap-2 sm:gap-2">
            <AlertDialogCancel disabled={deleting} className="rounded-full border-[var(--gt-border-strong)] bg-white">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={event => { event.preventDefault(); void confirmDelete(); }} disabled={deleting} className="rounded-full bg-red-700 text-white hover:bg-red-800">{deleting ? 'Deleting…' : 'Delete meeting'}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}

export default MeetlyMeetingList;
